import { Injectable } from '@angular/core';
import { OnfidoProvider } from '../../providers/myria/onfido-provider';
import { SendFlowProvider } from '../../providers/send-flow/send-flow';
import { OrderProvider } from '../../providers/order/order';
import { RouteProvider } from '../../providers/route/route';
import { LoadingProvider } from '../../providers/loading/loading';
import { LoggerProvider, LoggerEvent } from '../../providers/logger/logger';
import { SegmentProvider } from "../../providers/analytics/segment";
import { WalletProvider } from "../../providers/wallet/wallet";
import { QrcodescannerProvider } from "../../providers/qrcodescanner/qrcodescanner";
import { PinActionType } from '../../model/types/wallet-pin-action-type';
import { APP_CONFIG, Region } from '../../environment';
import { QRCodeInfo, QrIDtype } from "../../model/models/wallet/wallet";
import { AnalyticsEventType, PasscodeChallengeLocation } from '../../model/types/analytics.const';
import { COUNTRY_SETTINGS } from '../../providers/myria/app-variables';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { StorageProvider } from '../storage/storage';
import { AuthProvider } from '../authorization';
import { HttpProvider } from '../http/http';
import { StorageType } from '../../model/types/storage-type';
import { TranslateService } from '@ngx-translate/core';
import { KYCProvider } from '../myria/kyc-provider';
import { WalletVerificationStatus } from '../../model/types/wallet-verification-status';
@Injectable()
export class CommonProvider {

  constructor(private onfidoProvider: OnfidoProvider,
              private sendFlowProvider: SendFlowProvider,
              private orderProvider: OrderProvider,
              private routeProvider: RouteProvider,
              private loadingProvider: LoadingProvider,
              private analytics: SegmentProvider,
              private walletProvider: WalletProvider,
              private qrcodescannerProvider: QrcodescannerProvider,
              private utilsProvider: UtilsProvider,
              private storageProvider: StorageProvider,
              private authProvider: AuthProvider,
              private httpProvider: HttpProvider,
              private translate: TranslateService,
              private kycProvider: KYCProvider
            ) {
    
  }
  async openSendActionSheet(walletDetails, location, isSNUserKyced = false, isCustomerIDExpired = null, p2pDisabled = false) {
    this.routeProvider.openActionSheet(this.routeProvider.pages.TabSendMorePage, { p2pDisabled }, async (item) => {
      if (item === "wallet") {
        this.onSendOrRequestClick(walletDetails, location);
      } else if (item === "sendmoney") {
        const liteWalletInflowLimit = this.utilsProvider.getAmountWithCurrency(this.walletProvider.liteWalletInflowLimit, walletDetails.WalletCurrencyCode);

  
        let isSNWalletNotVerified = false
        if (this.isSN() && (!isSNUserKyced || isCustomerIDExpired || !walletDetails.IsKycEd)) {
          const pageToRender = !isCustomerIDExpired ? this.routeProvider.pages.EkycRequiredPage : this.routeProvider.pages.EkycExpiredPage;

          this.loadingProvider.show();
          const status = await this.walletProvider.getLiteWalletVerificationStatus(this.kycProvider)
          isSNWalletNotVerified = status !== WalletVerificationStatus.Success
    
          if((status == WalletVerificationStatus.InProgress || status == WalletVerificationStatus.Important) && !isCustomerIDExpired) {
            this.routeProvider.openModal(this.routeProvider.pages.OnfidoPendingPage);
            this.loadingProvider.hide();
            return
          }

          this.routeProvider.openModal(pageToRender, {
            msg: this.translate.instant("page.identityVerificationPage.verificationRequiredBody", { liteWalletInflowLimit }),
            primaryButtonAction: () => this.walletProvider.verifyIdFromWallet(this.kycProvider, 'Wallet')
          });

          this.loadingProvider.hide();
          return;
        }
        this.openSendMoney();
      }
    }, 'rmt-select-actionsheet-modal');
  }

  async openSendMoney() {
    const storedOrder = await this.orderProvider.getStoredOrder();
    if (storedOrder && storedOrder.activityDetails && storedOrder.activityDetails.page) {
      if (storedOrder.pendingEKyc || storedOrder.onfidoId) {
        this.loadingProvider.show();
        try {
          this.onfidoProvider.checkId = storedOrder.onfidoId;
          const res = await this.onfidoProvider.getOnfidoStatus(false);
          this.onfidoProvider.handleResult(res, storedOrder, true);
          this.loadingProvider.hide();
          return;
        }
        catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to get Onfido status in send money');
        }
        this.loadingProvider.hide();
      }
      this.routeProvider.openModal(storedOrder.activityDetails.page);
    }
    else {
      this.sendFlowProvider.sendMoneyBtn();
    }
  }
  
  onSendOrRequestClick(walletDetails, location){
    this.routeProvider.openModal(this.routeProvider.pages.RecipientListPage, { actionType: PinActionType.SendOrRequest, WalletDetails: walletDetails, location: location });
  }

  isSN() {
    if(APP_CONFIG.region.region === Region.SN){
      return true;
    }
    return false;
  }

  async openWithdrawalScanner(){
    try {
      let barCodeObj = await this.qrcodescannerProvider.initializeAndOpenQRScanner(PinActionType.Withdraw, false, false);
      if(barCodeObj && barCodeObj.barCode !== null && barCodeObj.barCode !== ""){
        LoggerProvider.info(this, 'Scanning Withdraw with QR Code');
        this.processQrCode(barCodeObj.barCode)
      } else {
        this.walletProvider.showQRGenericError();
      }
    } catch (ex) {
      LoggerProvider.info(this, 'scanner closed or permission denied for Withdraw QR Code');
    }
  }

  async processQrCode(barcodeData) {
    if (!barcodeData) return;
    this.loadingProvider.show();
    let qrCodeInfo: QRCodeInfo = await this.walletProvider.validateQRCode(barcodeData);
    if (qrCodeInfo && qrCodeInfo.QrCodeProvider === 1  && (qrCodeInfo.IdType === QrIDtype.Merchant || qrCodeInfo.IdType === QrIDtype.RiaStore)) {
      // Fire fees prefetch in parallel with the route pushh so it's in-flight while the next page mountss
      if (this.isSN() && qrCodeInfo.TransactionAmount) {
        const prefetchAmount = parseFloat(qrCodeInfo.TransactionAmount);
        const prefetchType = this.walletProvider.deriveQrTransactionType(qrCodeInfo, PinActionType.Withdraw);
        this.walletProvider.prefetchFees(prefetchAmount, prefetchType);
      }
      this.routeProvider.push(this.routeProvider.pages.WalletWithdrawOrPaymentPage, { showBack: true, actionType: PinActionType.Withdraw, QRCodeInfo: qrCodeInfo, WalletDetails: await this.walletProvider.getWalletDetails() });
    } else {
      this.walletProvider.showQRGenericError();
    }
    this.loadingProvider.hide();
  }

  logP2PSendStartedEvent(location, senderWalletId) {
    this.analytics.trackEvent({ eventName: AnalyticsEventType.P2PSendStarted, analyticsData: { country: APP_CONFIG.region.defaultCountryFrom, location: location, senderWalletId: senderWalletId } });
  }

  logPassCodeRecoveryFailedEvent(result, errorMessage){
    let failureReason = "";
    let eKYCStatus = false;
    if(result){
      if(result.Result){
        failureReason = result.Result.RetMsg ? result.Result.RetMsg : '';
        eKYCStatus = (result.Value && result.Value.KycDone) ? result.Value.KycDone : false;
      } else if(result.message){
        failureReason = result.message;
      }
    } else{
      failureReason = errorMessage;
    }
    this.analytics.trackEvent({ eventName: AnalyticsEventType.PasscodeRecoveryFailed, analyticsData: {country: APP_CONFIG.region.defaultCountryFrom, isEKYC: eKYCStatus, failureReason: failureReason}});
  } 

  logPassCodeChallengeStartedEvent(location: PasscodeChallengeLocation){
    this.analytics.trackEvent({eventName:AnalyticsEventType.PasscodeChallengeStarted ,analyticsData:{country: APP_CONFIG.region.defaultCountryFrom, location:location}});
  }

  logPassCodeChallengeSuccessfulEvent(location: PasscodeChallengeLocation){
    this.analytics.trackEvent({eventName:AnalyticsEventType.PasscodeChallengeSuccessful ,analyticsData:{country: APP_CONFIG.region.defaultCountryFrom, location:location}});
  }
  logPassCodeChallengeFailedEvent(location: PasscodeChallengeLocation, failureReason, errorCode?){
    this.analytics.trackEvent({ eventName: AnalyticsEventType.PasscodeChallengeFailed, analyticsData: { 
      country: APP_CONFIG.region.defaultCountryFrom, location: location, failureReason: failureReason,
      errorCode: (errorCode !== undefined && errorCode !== null) ? String(errorCode) : ''
     } });
  }

  logPassCodeUpdateFailedEvent(result, errorMessage){
    let failureReason = "";
    if(result){
      if(result.Result){
        failureReason = result.Result.RetMsg ? result.Result.RetMsg : '';
      } else if(result.message){
        failureReason = result.message;
      }
    } else{
      failureReason = errorMessage;
    }
    this.analytics.trackEvent({ eventName: AnalyticsEventType.PasscodeUpdateFailed, analyticsData: {failureReason: failureReason}});
  }

    // Function to check if biometrics is enabled and if so, to attempt to use biometrics for authentication
    async checkAndAuthViaBiometrics() {
      let fingerprintToken;
  
      const deviceToken = await this.storageProvider.get(StorageType.FingerprintDeviceToken); // controls isBiometricEnabled
      await this.httpProvider.waitForConnection();
      if (deviceToken && deviceToken.length > 0) {
        try {
          const available = await this.authProvider.biometricsAvailable();
          if (available) {
            const faceId = this.authProvider.faceId;
            const touchId = this.authProvider.touchId;
            const method = (faceId) ? 'faceID' : 'touchID';
            await new Promise<void>((resolve) => { window.setTimeout(() => { resolve(); }, 500); }); // delay 500ms so screen has a chance to show
  
            if (!fingerprintToken) {
              let timeout = window.setTimeout(() => {
                timeout = null;
                LoggerProvider.warning(this, LoggerEvent.PageException, 'Timeout for biometrics - hide prompt');
              }, 5000);
              fingerprintToken = await this.authProvider.getStoredFingerprintToken(method, false);
              if (timeout)
                window.clearTimeout(timeout);
            }
            if (fingerprintToken) {
              return fingerprintToken;
            }
            return null;
          }
        }
        catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.PageException, 'Failed to check biometrics in prelogin page');
        }
      }
      return null;
    }

}


// WEBPACK FOOTER //
// ./src/providers/common/common.ts